import { createSelector, createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import {
  DistributionListGroup,
  DistributionListGroupCreationRequest,
  DistributionListGroupFilters,
  DistributionListGroupUpdateRequest,
  emptyDistributionListGroupFilters,
  getEmptyGroup,
} from '../models/distributionListGroup';
import type { AppThunk, RootState } from '../../../core/store';
import api from '../utils/api';
import { toastService } from '../../../core/services/toastService';


interface DistributionListGroupSliceState {
  distributionListGroups: DistributionListGroup[];
  isFetching: boolean;
  error: string;
  filter: string;
  showResults: boolean;
  filters: DistributionListGroupFilters;
}

const initialState: DistributionListGroupSliceState = {
  distributionListGroups: [],
  isFetching: false,
  error: '',
  filter: '',
  showResults: true,
  filters: emptyDistributionListGroupFilters,
};

export const distributionListGroupSlice = createSlice({
  name: 'distributionListGroup',
  initialState,
  reducers: {
    startFetch: (state: Draft<DistributionListGroupSliceState>): DistributionListGroupSliceState => ({
      ...state,
      isFetching: true,
    }),
    finishFetchGroupList: (
      state: Draft<DistributionListGroupSliceState>,
      action: PayloadAction<DistributionListGroup[]>
    ) => ({
      ...state,
      isFetching: false,
      distributionListGroups: action.payload,
    }),
    finishFetchGroup: (
      state: Draft<DistributionListGroupSliceState>,
      action: PayloadAction<DistributionListGroup>
    ) => ({
      ...state,
      isFetching: false,
      distributionListGroups: [...state.distributionListGroups, action.payload],
      error: ''
    }),
    updateGroup: (state: Draft<DistributionListGroupSliceState>, action: PayloadAction<DistributionListGroup>) => ({
      ...state,
      isFetching: false,
      distributionListGroups: state.distributionListGroups.map(group =>
        group.id === action.payload.id ? action.payload : group
      ),
      error: '',
    }),
    removeEmptyGroup: (state: Draft<DistributionListGroupSliceState>) => {
      return {
        ...state,
        distributionListGroups: state.distributionListGroups.filter((group: DistributionListGroup) => group.id !== ''),
      };
    },
    addEmptyGroup: (state: Draft<DistributionListGroupSliceState>) => {
      return {
        ...state,
        distributionListGroups: state.distributionListGroups.find(group => group.id === '') != null ? [...state.distributionListGroups] : [getEmptyGroup(), ...state.distributionListGroups],
      };
    },
    removeGroup: (state: Draft<DistributionListGroupSliceState>, action: PayloadAction<string>) => ({
      ...state,
      isFetching: false,
      distributionListGroups: state.distributionListGroups.filter(group => group.id !== action.payload),
      error: '',
    }),
    httpError: (
      state: Draft<DistributionListGroupSliceState>,
      action: PayloadAction<string>
    ): DistributionListGroupSliceState => ({
      ...state,
      isFetching: false,
      error: action.payload,
    }),
    reset: () => initialState,
    setFilter: (
      state: Draft<DistributionListGroupSliceState>,
      action: PayloadAction<string>
    ): DistributionListGroupSliceState => ({
      ...state,
      filter: action.payload,
    }),
    setShowResults: (state: Draft<DistributionListGroupSliceState>, { payload }: PayloadAction<boolean>) => ({
      ...state,
      showResults: payload,
    }),
    setFilters: (state: Draft<DistributionListGroupSliceState>, { payload }: PayloadAction<DistributionListGroupFilters>) => ({
      ...state,
      filters: payload,
    }),
  },
});

export const {
  startFetch,
  finishFetchGroupList,
  finishFetchGroup,
  updateGroup,
  removeEmptyGroup, 
  addEmptyGroup,
  removeGroup,
  reset,
  httpError,
  setFilter,
  setShowResults,
  setFilters
} = distributionListGroupSlice.actions;

export const fetchDistributionListGroups = (): AppThunk => async dispatch => {
  dispatch(startFetch());
  try {
    const distributionListGroups = await api.fetchDistributionListGroups();
    dispatch(finishFetchGroupList(distributionListGroups));
  } catch (error) {
    dispatch(httpError(JSON.stringify(error)));
  }
};

export const createDistributionListGroup =
  (distributionListGroupCreationRequest: DistributionListGroupCreationRequest): AppThunk =>
  async dispatch => {
    dispatch(startFetch());
    try {
      const distributionListGroup = await api.createDistributionListGroups(distributionListGroupCreationRequest);
      dispatch(finishFetchGroup(distributionListGroup));
      toastService.success();
    } catch (error) {
      dispatch(httpError(JSON.stringify(error)));
    }
  };

export const updateDistributionListGroup =
  (id: string, distributionListGroupUpdateRequest: DistributionListGroupUpdateRequest): AppThunk =>
  async dispatch => {
    dispatch(startFetch());
    try {
      const distributionListGroup = await api.updateDistributionListGroup(id, distributionListGroupUpdateRequest);
      dispatch(updateGroup(distributionListGroup));
      toastService.success();
    } catch (error) {
      dispatch(httpError(JSON.stringify(error)));
    }
  };
export const deleteDistributionListGroup =
  (id: string): AppThunk =>
  async dispatch => {
    dispatch(startFetch());
    try {
      await api.deleteDistributionListGroup(id);
      dispatch(removeGroup(id));
      toastService.success();
    } catch (error) {
      dispatch(httpError(JSON.stringify(error)));
    }
  };

export const searchByText =
  (textFilter: string): AppThunk =>
  async dispatch => {
    dispatch(setFilter(textFilter));
  };

export const selectDistributionListGroups = (state: RootState): DistributionListGroup[] =>
  state.distributionListGroup.distributionListGroups;

  export const selectSortedDistributionListGroups = createSelector(
    [selectDistributionListGroups],
    (groups: DistributionListGroup[]) =>
      [...groups].sort((a, b) => a.name.localeCompare(b.name))
  );

export const selectIsFetchingDistributionListGroups = (state: RootState): boolean =>
  state.distributionListGroup.isFetching;

export const selectFilter = (state: RootState): string => state.distributionListGroup.filter;

export const selectShowResults = (state: any) => state.distributionListGroup.showResults;

export const selectFilters = (state: any) => state.distributionListGroup.filters;

export const selectFilteredDistributionGroups = createSelector(
  [selectSortedDistributionListGroups, selectFilter, selectFilters],
  (distributionListGroups: DistributionListGroup[], textFilter: string, filters: DistributionListGroupFilters): DistributionListGroup[] => {
    return distributionListGroups.filter((distributionListGroup: DistributionListGroup) => {
      const nameSearchMatch = isIncluded(distributionListGroup.name, textFilter);
      const brandSearchMatch = isIncluded(distributionListGroup.brand.description, textFilter);
      const regionSearchMatch = isIncluded(distributionListGroup.region.description, textFilter);
      const emailSearchMatch = distributionListGroup.distributionList.find(contact => isIncluded(contact.email, textFilter));

      const brandMatch = filters.brands.length === 0 || filters.brands.includes(distributionListGroup.brand.code) || distributionListGroup.brand == null;
      const regionMatch = filters.regions.length === 0 || filters.regions.includes(distributionListGroup.region.description) || distributionListGroup.region == null;
      const countryMatch = filters.countries.length === 0 ||
        distributionListGroup.countries?.some(country =>
          filters.countries.map(c => c.code).includes(country.code)
        ) ||
        distributionListGroup.countries == null;

      const result = (nameSearchMatch || brandSearchMatch || regionSearchMatch || emailSearchMatch) && brandMatch && regionMatch && countryMatch;

      return result;
    });
  }
);

const isIncluded = (value: string, textFilter: string): boolean =>
  value.toLowerCase().includes(textFilter.toLowerCase());

  export const isGroupNameUnique = (
    groupName: string,
    brandCode: string,
    groups: DistributionListGroup[],
    id: string | null
  ): boolean => {

    const result = groups.reduce((acc, v, index) => {
      const sameName = v.name.toLowerCase() === groupName.toLowerCase();
      const differentId = v.id !== id;
      const sameBrandCode = v.brand.code === brandCode;
      const conflict = sameName && differentId && sameBrandCode;
      return !conflict && acc;
    }, true);
  
    return result;
  };

export default distributionListGroupSlice.reducer;